import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Trash2, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS } from "../lib/bookingStatus";

function MemberRow({ member, onChanged }) {
  const [chatId, setChatId] = useState(member.telegram_chat_id || "");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { setChatId(member.telegram_chat_id || ""); }, [member.telegram_chat_id]);

  const dirty = (chatId.trim() || "") !== (member.telegram_chat_id || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/telegram/team/${member.id}`, { telegram_chat_id: chatId.trim() || null });
      toast.success(chatId.trim() ? "Chat-ID gespeichert." : "Chat-ID entfernt.");
      await onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await api.put(`/admin/telegram/team/${member.id}`, { telegram_chat_id: null });
      toast.success("Verknüpfung entfernt.");
      await onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setRemoving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.post(`/admin/telegram/team/${member.id}/test`);
      toast.success("Test-Nachricht gesendet — bitte Telegram prüfen.");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Fehler beim Senden."); }
    finally { setTesting(false); }
  };

  return (
    <div
      className="bg-white border border-navy/10 p-5 md:p-6"
      data-testid={`tg-member-${member.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg text-navy truncate" data-testid={`tg-member-name-${member.id}`}>
              {member.name || member.email}
            </span>
            <span
              className="px-2 py-0.5 text-[9.5px] tracking-[0.16em] uppercase border shrink-0"
              style={{ borderColor: `${BRAND_COLORS.navy}30`, color: `${BRAND_COLORS.navy}99` }}
              data-testid={`tg-member-role-${member.id}`}
            >
              {member.role === "admin" ? "Admin" : "Concierge"}
            </span>
          </div>
          <div className="text-[12.5px] text-navy-dark/60 mt-0.5 truncate">{member.email}</div>
        </div>
        <span
          className="inline-flex items-center gap-2 px-3 py-1 text-[10px] tracking-[0.18em] uppercase border shrink-0"
          style={member.linked
            ? { borderColor: `${BRAND_COLORS.greenDeep}40`, color: BRAND_COLORS.greenDeep }
            : { borderColor: `${BRAND_COLORS.navy}25`, color: `${BRAND_COLORS.navy}66` }}
          data-testid={`tg-member-status-${member.id}`}
        >
          <span className="w-1.5 h-1.5" style={{ background: member.linked ? BRAND_COLORS.greenDeep : `${BRAND_COLORS.navy}55` }} />
          {member.linked ? "Verknüpft" : "Nicht verknüpft"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-2.5">
        <input
          data-testid={`tg-member-input-${member.id}`}
          value={chatId}
          onChange={(e) => setChatId(e.target.value)}
          placeholder="Telegram-Chat-ID (z. B. 959862760)"
          className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-[14px]"
        />
        <button
          data-testid={`tg-member-save-${member.id}`}
          onClick={save}
          disabled={saving || !dirty}
          className="text-ivory px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          style={{ background: BRAND_COLORS.navy }}
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Speichern
        </button>
        <button
          data-testid={`tg-member-test-${member.id}`}
          onClick={test}
          disabled={testing || !member.linked}
          className="border border-gold text-navy px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:bg-gold/10 disabled:opacity-40 inline-flex items-center justify-center gap-2"
        >
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.8} />} Test
        </button>
        <button
          data-testid={`tg-member-remove-${member.id}`}
          onClick={remove}
          disabled={removing || !member.linked}
          className="border border-navy/25 text-navy-dark/70 px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:border-red-400 hover:text-red-500 disabled:opacity-30 inline-flex items-center justify-center gap-2"
          aria-label="Verknüpfung entfernen"
        >
          {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} />}
        </button>
      </div>
    </div>
  );
}

export default function TeamTelegramSettings() {
  const [members, setMembers] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/telegram/team");
      setMembers(data?.members || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
      setMembers([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const admins = (members || []).filter((m) => m.role === "admin");
  const concierges = (members || []).filter((m) => m.role === "concierge");

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-telegram-settings">
      <div className="mb-8">
        <div className="label-caps mb-3">Einstellungen · Benachrichtigungen</div>
        <h1 className="font-display text-3xl md:text-4xl text-navy">Telegram-Benachrichtigung</h1>
        <p className="text-[14px] text-navy-dark/70 mt-3 max-w-2xl">
          Verwalten Sie hier die Telegram-Verknüpfung für jedes Team-Konto. Kunden-Support-Nachrichten
          gehen an alle <strong>Admins</strong> mit verknüpfter Chat-ID. Jede Chat-ID lässt sich
          jederzeit setzen, ändern, entfernen und testen.
        </p>
      </div>

      {members === null ? (
        <div className="flex items-center gap-2 text-navy-dark/60 text-[14px]" data-testid="tg-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Lädt …
        </div>
      ) : (
        <div className="space-y-8">
          <section data-testid="tg-group-admins">
            <div className="label-caps mb-3 text-navy-dark/70">
              Admins <span className="normal-case tracking-normal text-navy-dark/45">· empfangen Kunden-Support</span>
            </div>
            <div className="space-y-3">
              {admins.length
                ? admins.map((m) => <MemberRow key={m.id} member={m} onChanged={load} />)
                : <div className="text-[13px] text-navy-dark/55">Keine Admin-Konten gefunden.</div>}
            </div>
          </section>

          <section data-testid="tg-group-concierges">
            <div className="label-caps mb-3 text-navy-dark/70">Concierges</div>
            <div className="space-y-3">
              {concierges.length
                ? concierges.map((m) => <MemberRow key={m.id} member={m} onChanged={load} />)
                : <div className="text-[13px] text-navy-dark/55">Keine Concierge-Konten gefunden.</div>}
            </div>
          </section>

          <details className="text-[12.5px] text-navy-dark/70">
            <summary className="cursor-pointer hover:text-navy">So findet ein Team-Mitglied seine Chat-ID (2 Min.)</summary>
            <ol className="mt-3 pl-5 space-y-1 list-decimal">
              <li>In Telegram <a href="https://t.me/Weissblom_concierge_bot" target="_blank" rel="noreferrer" className="text-navy border-b border-gold">@Weissblom_concierge_bot</a> öffnen und <strong>START</strong> klicken</li>
              <li>Eine kurze Nachricht an den Bot senden</li>
              <li>Im Browser <code className="bg-navy/5 px-1">api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> öffnen</li>
              <li>Den Wert <code className="bg-navy/5 px-1">chat.id</code> kopieren und hier eintragen</li>
            </ol>
          </details>
        </div>
      )}
    </div>
  );
}
